import { motion } from "framer-motion";
import { useLocation } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const gradText = "bg-[linear-gradient(92deg,#4BF4B9,#86F0B4)] bg-clip-text text-transparent";

const nodes = [
  { label: "Shopify", left: "50%", top: "12%" },
  { label: "Meta", left: "86.2%", top: "31.3%" },
  { label: "Google Ads", left: "86.2%", top: "72.9%" },
  { label: "TikTok Ads", left: "50%", top: "89.6%" },
  { label: "Klaviyo", left: "13.8%", top: "72.9%" },
  { label: "GA4 & data", left: "13.8%", top: "31.3%" },
];

/**
 * Visuele Signal-secties, hergebruikt van /werkwijze: de "Alles verbonden"-hub
 * en de Signal-feature met browser-mock. Gebruikt op de /signal-lander.
 */
export default function SignalShowcase() {
  const { language, localePath } = useLanguage();
  const nl = language === "nl";
  const [, setLocation] = useLocation();

  const signalPoints = [
    {
      title: nl ? "Je concurrentie in beeld" : "Your competition in view",
      text: nl
        ? "Met Signal scannen we razendsnel honderden ads van jouw merk én je concurrenten. Zo zien we welke angles, formats en copy-patronen werken en waar de top spenders in jouw categorie op inzetten."
        : "With Signal we rapidly scan hundreds of ads from your brand and your competitors. We see which angles, formats and copy patterns work and what the top spenders in your category are betting on.",
    },
    {
      title: nl ? "Testkansen voor morgen" : "Test opportunities for tomorrow",
      text: nl
        ? "Elke analyse vertalen we naar concrete tests: bewezen kansen uit je eigen data en nieuwe angles uit je markt. Die zetten we direct om in creatives en campagnes."
        : "We translate every analysis into concrete tests: proven opportunities from your own data and new angles from your market. We turn those directly into creatives and campaigns.",
    },
    {
      title: nl ? "Elke advertentie geanalyseerd" : "Every ad analyzed",
      text: nl
        ? "Per ad zien we bereik, looptijd en prestatie. Zo weten we precies welke creatives we moeten ontwikkelen en testen."
        : "Per ad we see reach, runtime and performance. So we know exactly which creatives to develop and test.",
    },
  ];

  return (
    <>
      {/* ─── Alles verbonden ─── */}
      <section className="relative bg-[#04100A] py-28 overflow-hidden">
        <div className="absolute top-[44%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[620px] h-[620px] bg-[radial-gradient(circle,rgba(75,244,185,0.10)_0%,rgba(75,244,185,0)_66%)] pointer-events-none" />
        <div className="relative max-w-[1240px] mx-auto px-6 md:px-10">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="text-center max-w-[760px] mx-auto">
            <h2 className="font-display font-extrabold text-[clamp(38px,5.2vw,66px)] tracking-[-0.04em] leading-none">
              {nl ? "Alles verbonden." : "Everything connected."}<br />
              <span className={gradText}>{nl ? "Alles meetbaar." : "Everything measurable."}</span>
            </h2>
            <p className="text-lg leading-relaxed text-white/70 max-w-[52ch] mx-auto mt-6">
              {nl
                ? "Wrkt Signal koppelt je shop, kanalen en CRM, leest je markt en voedt elke beslissing met data. Jij ziet het resultaat in één dashboard."
                : "Wrkt Signal connects your shop, channels and CRM, reads your market and feeds every decision with data. You see the result in one dashboard."}
            </p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} className="relative w-full max-w-[780px] h-[380px] md:h-[480px] mx-auto mt-14" aria-hidden="true">
            <svg viewBox="0 0 780 480" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
              <g stroke="rgba(75,244,185,0.3)" strokeWidth="1.5" strokeDasharray="2 7" strokeLinecap="round" fill="none">
                <line x1="390" y1="240" x2="390" y2="58" />
                <line x1="390" y1="240" x2="672" y2="150" />
                <line x1="390" y1="240" x2="672" y2="350" />
                <line x1="390" y1="240" x2="390" y2="430" />
                <line x1="390" y1="240" x2="108" y2="350" />
                <line x1="390" y1="240" x2="108" y2="150" />
              </g>
            </svg>
            <div className="has-grain absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140px] h-[140px] md:w-[168px] md:h-[168px] rounded-full flex flex-col items-center justify-center text-center border border-[#4BF4B9]/40"
              style={{ background: "radial-gradient(farthest-corner at 60% 30%, #1E6B41, #06150C 82%)", boxShadow: "0 0 60px rgba(75,244,185,0.28), inset 0 1px 0 rgba(255,255,255,0.1)" }}>
              <div className="font-display font-extrabold text-[22px] tracking-[-0.02em] text-white">Wrkt Signal</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4BF4B9] mt-1">{nl ? "AI-kern" : "AI core"}</div>
            </div>
            {nodes.map((node, i) => (
              <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 px-4 py-2.5 md:px-[22px] md:py-3 rounded-[14px] bg-white/[0.04] border border-white/10 font-display font-bold text-[12px] md:text-[14.5px] text-white/90 whitespace-nowrap"
                style={{ left: node.left, top: node.top }}>
                {node.label}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── Wrkt Signal feature + browser mock ─── */}
      <section className="relative bg-black py-28">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-[0.92fr_1.08fr] gap-16 items-center">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }}>
            <p className="text-[#4BF4B9] text-xs font-semibold uppercase tracking-[0.2em]">
              {nl ? "Eigen AI-tooling" : "Our own AI tooling"}
            </p>
            <h2 className="font-display font-extrabold text-[clamp(34px,4.4vw,54px)] tracking-[-0.035em] leading-[1.02] mt-4">
              {nl ? "Blijf de concurrentie voor." : "Stay ahead of the competition."}<br />
              <span className={gradText}>{nl ? "Met Wrkt Signal." : "With Wrkt Signal."}</span>
            </h2>
            <div className="flex flex-col gap-7 mt-10">
              {signalPoints.map((point, i) => (
                <div key={i} className="flex gap-[18px]">
                  <span className="flex-none w-[30px] h-[30px] rounded-full border border-[#4BF4B9]/35 bg-[#4BF4B9]/[0.08] text-[#4BF4B9] font-display font-bold text-[13px] flex items-center justify-center mt-0.5">{i + 1}</span>
                  <div>
                    <div className="font-display font-bold text-[19px] tracking-[-0.01em] text-white">{point.title}</div>
                    <p className="text-[15px] leading-[1.66] text-white/[0.64] mt-2 max-w-[48ch]">{point.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <button onClick={() => setLocation(localePath("/contact"))} className="btn btn--gradient btn--lg" data-testid="button-signal-lander-demo">
                {nl ? "Vraag een demo aan →" : "Request a demo →"}
              </button>
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} aria-hidden="true">
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0A1712]" style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.5), 0 0 60px rgba(20,70,41,0.25)" }}>
              <div className="flex items-center gap-2 px-4 py-[13px] bg-[#0B2116]/50 border-b border-white/[0.07]">
                <span className="w-[11px] h-[11px] rounded-full bg-white/[0.18]" />
                <span className="w-[11px] h-[11px] rounded-full bg-white/[0.18]" />
                <span className="w-[11px] h-[11px] rounded-full bg-white/[0.18]" />
                <span className="mx-auto px-4 py-[5px] rounded-full bg-white/[0.06] text-[11.5px] text-white/70 inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-[3px] inline-block" style={{ background: "var(--grad-green)" }} />signal.wrkt.nl
                </span>
              </div>
              <div className="p-5 flex flex-col gap-3.5">
                <div className="flex items-center gap-3">
                  <div className="h-[7px] w-[92px] rounded-full" style={{ background: "var(--grad-green)" }} />
                  <div className="h-[7px] flex-1 rounded-full bg-white/[0.07]" />
                  <div className="font-display font-extrabold text-[15px] text-[#4BF4B9]">83</div>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                    <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">{nl ? "Creative kaartje" : "Creative card"}</div>
                    <div className="flex flex-col gap-2 mt-3">
                      <div className="h-2 w-[88%] rounded-full bg-[#46A572]" />
                      <div className="h-2 w-[64%] rounded-full bg-[#2F8351]" />
                      <div className="h-2 w-[74%] rounded-full bg-[#4BF4B9]" />
                      <div className="h-2 w-[42%] rounded-full bg-white/[0.12]" />
                      <div className="h-2 w-[55%] rounded-full bg-[#2F8351]" />
                    </div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                    <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">Angle + format</div>
                    <div className="h-1.5 w-[90%] rounded-full bg-white/10 mt-3" />
                    <div className="h-1.5 w-[70%] rounded-full bg-white/[0.08] mt-2" />
                    <div className="flex gap-1.5 mt-3.5">
                      <span className="px-3 py-[5px] rounded-[7px] bg-[#4BF4B9]/[0.12] border border-[#4BF4B9]/25 text-[10px] font-bold text-[#4BF4B9]">Video</span>
                      <span className="px-3 py-[5px] rounded-[7px] bg-white/[0.05] border border-white/10 text-[10px] font-bold text-white/60">Static</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                  <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">{nl ? "Copy patronen" : "Copy patterns"}</div>
                  <div className="grid grid-cols-4 gap-3 mt-3">
                    <div><div className="font-display font-extrabold text-lg text-white">13</div><div className="h-[5px] w-[80%] rounded-full bg-[#46A572] mt-1.5" /></div>
                    <div><div className="font-display font-extrabold text-lg text-white">61%</div><div className="h-[5px] w-[60%] rounded-full bg-[#2F8351] mt-1.5" /></div>
                    <div><div className="font-display font-extrabold text-lg text-white">38%</div><div className="h-[5px] w-[40%] rounded-full bg-white/[0.14] mt-1.5" /></div>
                    <div><div className="font-display font-extrabold text-lg text-[#4BF4B9]">5.8×</div><div className="h-[5px] w-[90%] rounded-full bg-[#4BF4B9] mt-1.5" /></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                    <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">Top spenders</div>
                    <div className="flex flex-col gap-2.5 mt-3">
                      <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-[5px] bg-gradient-to-br from-[#256C43] to-[#4BF4B9]" /><div className="h-1.5 flex-1 rounded-full bg-white/10" /></div>
                      <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-[5px] bg-gradient-to-br from-[#1B5234] to-[#38B083]" /><div className="h-1.5 flex-1 rounded-full bg-white/[0.08]" /></div>
                      <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-[5px] bg-gradient-to-br from-[#123420] to-[#2F8351]" /><div className="h-1.5 flex-1 rounded-full bg-white/[0.08]" /></div>
                    </div>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3.5">
                    <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42]">{nl ? "Landingspagina's" : "Landing pages"}</div>
                    <div className="flex flex-col gap-2 mt-3">
                      <div className="h-[7px] w-[85%] rounded-full bg-[#E0A43B]" />
                      <div className="h-[7px] w-[55%] rounded-full bg-[#E0A43B]/40" />
                      <div className="h-[7px] w-[68%] rounded-full bg-white/10" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/[0.42] mb-2.5">{nl ? "Top presterende creatives" : "Top performing creatives"}</div>
                  <div className="grid grid-cols-3 gap-3">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="aspect-[3/4] rounded-[10px] border border-white/[0.07] flex items-end p-2.5"
                        style={{ background: `linear-gradient(${160 + i * 25}deg, rgba(75,244,185,${0.14 - i * 0.03}), rgba(6,21,12,0.9) 70%)` }}>
                        <div className="w-full">
                          <div className="h-1.5 w-3/4 rounded-full bg-white/20" />
                          <div className="h-1.5 w-1/2 rounded-full bg-[#4BF4B9]/50 mt-1.5" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
